import { types, flow, Instance, unprotect } from "mobx-state-tree";
import axios from "axios";
import { PARENTS_API, SCHEDULE_PERMISSION, STUDENTS_API } from "@constants/api";
import { AlertResultApi } from "@utils/alert";
import { fetching, FetchingEnum } from "./Fetching";
import { Pagination } from "./Pagination";
import { jsonParsePagination } from "@utils/helper";

export const initialSchedulePermission = {
  fetchingListChild: FetchingEnum.DONE,
  fetchingAddPermission: FetchingEnum.DONE,
  fetchingListPermissionParent: FetchingEnum.DONE,
  fetchRemovePermissionParent: FetchingEnum.DONE,
  fetchExistingParent: FetchingEnum.DONE,
  fetchingListChildMore: FetchingEnum.DONE,
  fetchRemovePermission: FetchingEnum.DONE,
};

const Child = types.model({
  id: types.number,
  schoolId: types.number,
  levelId: types.number,
  levelGroupId: types.maybeNull(types.number),
  firstName: types.string,
  lastName: types.string,
  gender: types.string,
  macAddress: types.maybeNull(types.string),
  photoUrl: types.maybeNull(types.string),
  dateOfBirth: types.string,
  // active, inactive, pending_renewal_confirmation
  status: types.string,
  isMain: types.boolean,
  permissionId: types.number,
  createdAt: types.string,
  updatedAt: types.string,
});

const PermissionParent = types.model({
  id: types.number,
  parentId: types.number,
  parent: types.model({
    id: types.number,
    firstName: types.string,
    lastName: types.string,
    email: types.string,
  }),
  studentId: types.number,
  student: types.model({
    id: types.number,
    firstName: types.string,
    lastName: types.string,
  }),
  isMain: types.boolean,
  createdAt: types.string,
  updatedAt: types.string,
});

const existingParent = types.model({
  id: types.number, // parentID
  permissionId: types.maybeNull(types.number), // get from getListPermissionParent
  user: types.model({
    id: types.number,
    email: types.string,
    mbcCustomerId: types.maybeNull(types.string),
  }),
  salutation: types.maybeNull(types.string),
  firstName: types.string,
  lastName: types.string,
  securityCode: types.maybeNull(types.string),
  mobile: types.maybeNull(types.string),
  vaNumber: types.maybeNull(types.string),
  status: types.maybeNull(types.string),
  createdAt: types.string,
  updatedAt: types.string,
});

export const SchedulePermissions = types
  .model({
    // Get List Childs
    fetchingListChild: fetching,
    fetchingListChildMore: fetching,
    listChildren: types.optional(types.array(Child), []),
    statusListChild: types.maybeNull(types.number),
    paginationListChild: types.maybeNull(Pagination),

    // Add permission to student
    fetchingAddPermission: fetching,
    statusAddPermission: types.maybeNull(types.number),

    // Get list permission parent in student
    fetchingListPermissionParent: fetching,
    listPermissionParents: types.maybeNull(types.array(PermissionParent)),
    statusListPermissionParent: types.maybeNull(types.number),

    // Remove permission parent in child
    fetchRemovePermissionParent: fetching,
    statusRemovePermissionParent: types.maybeNull(types.number),

    // Check existing Parent by Email
    fetchExistingParent: fetching,
    existingParent: types.maybeNull(existingParent),
    statusExistingParent: types.maybeNull(types.number),

    // Remove Permission
    fetchRemovePermission: fetching,
    statusRemovePermission: types.maybeNull(types.number),
  })
  .actions((self) => ({
    // initial state
    initialState: () => {
      self.paginationListChild = null;
      self.listPermissionParents = null;
      self.existingParent = null;
      self.statusRemovePermission = null;
      self.fetchRemovePermission = FetchingEnum.DONE;
      self.fetchingAddPermission = FetchingEnum.DONE;
      self.fetchingListPermissionParent = FetchingEnum.DONE;
      self.fetchRemovePermissionParent = FetchingEnum.DONE;
      self.fetchExistingParent = FetchingEnum.DONE;
      self.fetchingListChildMore = FetchingEnum.DONE;
      self.fetchRemovePermission = FetchingEnum.DONE;
    },
    /* get list childs */
    getListChilds: flow(function* (
      token: string,
      ParentId: number,
      pageNumber: number,
      PageSize: number = 10
    ) {
      // console.tresult.dataron.log(ParentId);
      self.statusListChild = null;
      self.fetchingListChild = FetchingEnum.PENDING;
      self.listChildren.replace([]);
      self.paginationListChild = null;

      try {
        const result = yield axios.get(SCHEDULE_PERMISSION, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            Filter: JSON.stringify({ parentId: ParentId }),
            PageSize: PageSize,
            PageNumber: pageNumber,
          },
        });

        self.fetchingListChild = FetchingEnum.DONE;
        self.fetchingListChildMore = FetchingEnum.DONE;
        self.statusListChild = result.status;
        self.listChildren.replace(result.data);
        self.paginationListChild = jsonParsePagination(
          result.headers["x-pagination"]
        );
      } catch (error) {
        // console.tron.log('Failed to fetch projects', error);

        // console.error('Failed to fetch projects', error.response);
        self.statusListChild = error.response.status;
        self.fetchingListChild = FetchingEnum.ERROR;
        self.fetchingListChildMore = FetchingEnum.ERROR;
        AlertResultApi(error?.response?.status, error.response?.data?.message);
      }
    }),

    // POST Add permission to student
    addPermission: flow(function* (
      token: string,
      parentId: number,
      studentId: number,
      callback: (data: string) => void
    ) {
      self.fetchingAddPermission = FetchingEnum.PENDING;
      self.statusAddPermission = null;
      try {
        const result = yield axios.post(
          `${STUDENTS_API}/${studentId}/permissions`,
          {
            parentId,
            studentId,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        self.fetchingAddPermission = FetchingEnum.DONE;
        self.statusAddPermission = result.status;
        callback("success");
      } catch (error) {
        callback("error");
        self.fetchingAddPermission = "error";
        self.statusAddPermission = error.response.status;
        AlertResultApi(
          error?.response?.status || 500,
          error.response?.data?.message
        );
      }
    }),

    // GET List permission parent in student
    getListPermissionParent: flow(function* (token: string, studentId: number) {
      self.fetchingListPermissionParent = FetchingEnum.PENDING;
      self.listPermissionParents = null;
      self.statusListPermissionParent = null;
      try {
        const result = yield axios.get(
          `${STUDENTS_API}/${studentId}/permissions?Query.Filter=${JSON.stringify(
            {
              IsMain: false,
            }
          )}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        self.listPermissionParents = result.data;
        self.fetchingListPermissionParent = FetchingEnum.DONE;
        self.statusListPermissionParent = result.status;
      } catch (error) {
        self.fetchingListPermissionParent = "error";
        self.statusListPermissionParent = error.response.status;
        AlertResultApi(
          error?.response?.status || 500,
          error.response?.data?.message
        );
      }
    }),

    // DELETE Remove parents parent in child
    removePermissionParent: flow(function* (
      token: string,
      studentId: number,
      permissionId: number,
      callback: () => void
    ) {
      self.fetchRemovePermissionParent = FetchingEnum.PENDING;
      self.statusRemovePermissionParent = null;
      try {
        const result = yield axios.delete(
          `${STUDENTS_API}/${studentId}/permissions/${permissionId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        self.fetchRemovePermissionParent = FetchingEnum.DONE;
        self.statusRemovePermissionParent = result.status;
        callback();
      } catch (error) {
        self.fetchRemovePermissionParent = "error";
        self.statusRemovePermissionParent = error.response.status;
        AlertResultApi(
          error?.response?.status || 500,
          error.response?.data?.message
        );
      }
    }),

    // POST Check existing Parent by Email
    checkExistingParent: flow(function* (
      token: string,
      email: string,
      callback: (data: ExistingParentType | string) => void
    ) {
      self.fetchExistingParent = FetchingEnum.PENDING;
      self.existingParent = null;
      self.statusExistingParent = null;
      try {
        const result = yield axios.post(
          `${PARENTS_API}/checkExistingParent`,
          {
            email,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        self.existingParent = result.data;
        self.fetchExistingParent = FetchingEnum.DONE;
        self.statusExistingParent = result.status;
        callback(result.data);
      } catch (error) {
        if (error.response.status === 404) {
          callback("not_found");
          self.statusExistingParent = 404;
          return;
        }
        self.fetchExistingParent = "error";
        self.statusExistingParent = error.response.status;
        AlertResultApi(
          error?.response?.status || 500,
          error.response?.data?.message
        );
        callback("error");
      }
    }),
    removePermission: flow(function* (
      token: string,
      studentId: number,
      permissionId: number,
      callback: (status: string) => void
    ) {
      self.fetchRemovePermission = FetchingEnum.PENDING;
      self.statusRemovePermission = null;
      try {
        const result = yield axios.delete(
          `${STUDENTS_API}/${studentId}/permissions/${permissionId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        self.fetchRemovePermission = FetchingEnum.DONE;
        self.statusRemovePermission = result.status;
        callback("success");
      } catch (error) {
        callback("error");
        self.fetchRemovePermission = "error";
        self.statusRemovePermission = error.response.status;
        AlertResultApi(
          error?.response?.status || 500,
          error.response?.data?.message
        );
      }
    }),
  }))
  .views((self) => ({
    get viewListPermissionParent() {
      return self.listPermissionParents ? self.listPermissionParents : [];
    },
    findChildById(id: number) {
      return self.listChildren?.find((child) => child.id === id) || null;
    },
    get findChildrenRenewal() {
      return (
        self.listChildren?.filter(
          (child) => child.status === "pending_renewal_confirmation"
        ) || null
      );
    },
  }));

export type ExistingParentType = Instance<typeof existingParent>;
export type ChildType = Instance<typeof Child>;
